import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import pluralize from 'ati-utils/pluralize';
import interpolate from 'ati-utils/interpolate';
import Skeleton from 'ati-ui-react/components/Skeleton';

import styles from './styles.modules.scss';
import i18nStrings from './strings';
import { useLocalized } from 'utils/useLocalized';
import { useDebounce } from 'utils/useDebounce';
import { DataContext, LoadingContext, LocaleContext } from 'contexts';
import MoreButton from './MoreButton';
import MobileResults from './MobileResults';
import DesktopResults from './DesktopResults';
import {useLocation, useNavigate} from 'react-router-dom';
import PropTypes from 'prop-types';


const SearchResults = ({showCount, showStep, setShowCount}) => {
    const strings = useLocalized(i18nStrings);
    const { searchResults, searchResultsParams, licenseInfo, processPay, isMobile, isUser, isOutsideRussia, writeScrollFirmsEvent } = useContext(DataContext);
    const { isGlobalLoading } = useContext(LoadingContext);
    const locale = useContext(LocaleContext);
    const [firmsIds, setFirmsIds] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();
    const totalCount = useMemo(() => searchResults?.result?.length || 0, [searchResults]);

    const availableMoreCount = useMemo(() => {
        const diff = Math.min((totalCount - showCount), showStep);
        return diff > 0 ? diff : 0;
    }, [totalCount, showCount, showStep]);

    const handleClickShowMore = useCallback(() => {
        setShowCount(showCount + availableMoreCount);
    }, [setShowCount, showCount, availableMoreCount]);

    const writeEventDebounce = useDebounce(2000, useCallback(() => {
        if (firmsIds.length > 0) {
            writeScrollFirmsEvent(firmsIds);
            setFirmsIds(ids => ids.filter(f => !firmsIds.includes(f)));
        }
    }, [firmsIds, writeScrollFirmsEvent]));

    useEffect(() => {
        if (firmsIds.length > 0) {
            writeEventDebounce();
        }
    }, [firmsIds, writeEventDebounce]);

    const handleChangeVisibility = useCallback((firmId) => setFirmsIds(ids => [...ids, firmId]), []);

    const moreBtnJSX = useMemo(() => {
        if (isGlobalLoading || availableMoreCount <= 0){
            return null;
        }

        if (licenseInfo.searchAccessFinishUtc || (isUser && isOutsideRussia)) {
            return (
                <MoreButton
                    count={availableMoreCount}
                    onClick={handleClickShowMore}
                />
            );
        }

        return (
            <MoreButton
                needPay
                count={availableMoreCount}
                onClick={() => {
                    processPay(navigate, location.search);
                }}
            />
        );
    }, [isGlobalLoading, isUser, availableMoreCount, licenseInfo, processPay, isOutsideRussia, handleClickShowMore, navigate, location.search]);

    const foundPluralized = useMemo(() => pluralize({
        zero: strings.found.zero,
        one: strings.found.one,
        few: strings.found.few,
        many: strings.found.many,
        other: strings.found.other,
    }, totalCount, locale), [strings, locale, totalCount]);

    const companiesPluralized = useMemo(() => pluralize({
        zero: strings.companies.zero,
        one: strings.companies.one,
        few: strings.companies.few,
        many: strings.companies.many,
        other: strings.companies.other,
    }, totalCount, locale), [strings, locale, totalCount]);

    const foundedTitle = useMemo(() => {
        const more = totalCount === 1000 ? strings.moreThan : '';
        return totalCount === 0 ? strings.noCompanies : `${foundPluralized} ${more} ${totalCount} ${companiesPluralized}`;
    }, [foundPluralized, companiesPluralized, totalCount, strings]);

    const direction = searchResultsParams ? interpolate(strings.direction, {
        from: searchResultsParams.geoFromName,
        to: searchResultsParams.geoToName,
        period: searchResultsParams.periodTitle
    }) : '';

    return (
        <div className={styles['results']}>
            <div className={styles['title']}>
                <div className={styles['founded']}>
                    {isGlobalLoading || !searchResults ? (
                        <Skeleton
                            borderRadius={2}
                            height={24}
                            width={isMobile ? '75%' : '25%'}
                        />
                    ) : foundedTitle}
                </div>
                <div className={styles['filters']}>
                    {isGlobalLoading || !searchResults ? (
                        <Skeleton
                            borderRadius={2}
                            height={20}
                            width={isMobile ? '100%' : '40%'}
                        />
                    ) : direction}
                </div>
            </div>
            {isMobile ? (
                <MobileResults
                    showCount={showCount}
                    onChangeVisibility={handleChangeVisibility}
                />
            ) : (
                <DesktopResults
                    showCount={showCount}
                    onChangeVisibility={handleChangeVisibility}
                />
            )}
            {moreBtnJSX}
        </div>
    );
};

SearchResults.propTypes = {
    setShowCount: PropTypes.func,
    showCount: PropTypes.number,
    showStep: PropTypes.number,
};

export default SearchResults;
